import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { getPushNotificationsApi } from 'api/notifications';
import { useAuth } from 'contexts/AuthContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import type { GetPushNotificationResponseDTO } from 'declarations/dto/notifications';
import {
  NotificationsPermissionsE,
  WIP_NOTIFICATION_ACTION_TYPES,
} from 'declarations/enums/notifications';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { RoutePathsE } from 'declarations/enums/routes';
import { StorageE } from 'declarations/enums/storage';
import { setSelectedWipRoofTopId, setWipSearchTerm } from 'reduxStore/features/wip/wipSlice';
import { useAppDispatch } from 'reduxStore/hooks';

const usePushNotifications = (): void => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const { isUserLoggedIn } = useAuth();
  const { userProfile } = useUserProfile();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const socketRef = useRef<WebSocket | null>(null);
  const notificationRef = useRef<Notification | null>(null);
  /**
   * Holds the most recent push payload so the (stable) click handler can read it without
   * re-registering as a new listener on every message. We need the payload to decide whether
   * to route to /wip (WARR-10276) versus the tickets page.
   */
  const latestPushRef = useRef<GetPushNotificationResponseDTO | null>(null);

  /**
   * Handlers
   */
  const handleNotificationClick = useCallback((): void => {
    const notification = latestPushRef.current;
    const isWipNotification =
      !!notification && WIP_NOTIFICATION_ACTION_TYPES.includes(notification.actionTypeId);

    if (isWipNotification && notification) {
      // Deep-link straight to the WIP tab and seed the dealer + RO search so the user lands
      // on the originating record (WARR-10276 "Click navigates directly to WIP item").
      if (notification.roofTopId) {
        dispatch(setSelectedWipRoofTopId(notification.roofTopId));
      }
      if (notification.ticket?.roNumber) {
        dispatch(setWipSearchTerm(notification.ticket.roNumber));
      }
      if (window.location.pathname !== RoutePathsE.WIP) {
        navigate(RoutePathsE.WIP);
      }
      window.focus();
      return;
    }

    if (window.location.pathname !== RoutePathsE.TICKETS_PAGE) {
      navigate(RoutePathsE.TICKETS_PAGE);
    }
    window.focus();
  }, [dispatch, navigate]);
  const displayNotification = useCallback(
    (notification: GetPushNotificationResponseDTO) => {
      latestPushRef.current = notification;
      const { action, sender } = notification;
      const notificationOptions = {
        body: action,
      };

      notificationRef.current = new Notification(sender.displayName, notificationOptions);
      notificationRef.current.addEventListener('click', handleNotificationClick);
    },
    [handleNotificationClick]
  );
  const handleSocketGetMessage = useCallback(
    (event: MessageEvent) => {
      const notification = JSON.parse(event.data) as GetPushNotificationResponseDTO;
      displayNotification(notification);

      if (notification.actionTypeId === 'MessageBoardUpdated') {
        queryClient.invalidateQueries([RequestQueryKeysE.FetchBannerMessages]);
      } else {
        queryClient.invalidateQueries([RequestQueryKeysE.FetchNotificationsHistory]);
      }
    },
    [displayNotification, queryClient]
  );
  const setupSocket = useCallback(() => {
    const idToken = localStorage.getItem(StorageE.IdToken);

    if (userProfile && idToken) {
      const { id: userId } = userProfile;

      socketRef.current = getPushNotificationsApi({ idToken, userId });
      socketRef.current.addEventListener('message', handleSocketGetMessage);
    }
  }, [userProfile, handleSocketGetMessage]);
  const askNotificationsPermission = useCallback(async () => {
    const permission = await Notification.requestPermission();

    if (permission === NotificationsPermissionsE.Granted) {
      setupSocket();
    }
  }, [setupSocket]);
  const setupNotifications = useCallback(() => {
    const isNotificationsSupported = 'Notification' in window;

    if (isNotificationsSupported) {
      Notification.permission === NotificationsPermissionsE.Granted
        ? setupSocket()
        : askNotificationsPermission();
    }
  }, [askNotificationsPermission, setupSocket]);

  /**
   * Effects
   */
  useEffect(() => {
    if (isUserLoggedIn && userProfile) {
      setupNotifications();
    }

    return () => {
      if (notificationRef.current && socketRef.current) {
        notificationRef.current.removeEventListener('click', handleNotificationClick);
        socketRef.current.removeEventListener('message', handleSocketGetMessage);
        socketRef.current.close();
      }
    };
  }, [
    isUserLoggedIn,
    userProfile,
    setupNotifications,
    handleSocketGetMessage,
    handleNotificationClick,
  ]);
};

export default usePushNotifications;
